import { toastWIP } from '@/components/WIP'
import { Register, type NavigateOptions } from '@tanstack/react-router'
import { MouseEvent, useCallback, useMemo } from 'react'
import { fetchOriginalPost, fetchRepost, getUniversalPostFetcher } from '../fetchers/post'
import { getUserProfile } from '../fetchers/user'
import parseJikeURLScheme from '../jikeURLScheme'
import { POST_TYPE_TO_ROUTE_MAPPING } from '../route'
import useUniversalNavigate from './useUniversalNavigate'

async function getNaviOptions(
	link: string | undefined
): Promise<NavigateOptions<Register['router']>> {
	if (!link) {
		throw new Error('No invalid linkUrl')
	}
	const scheme = parseJikeURLScheme(link)

	switch (scheme.type) {
		case 'USER': {
			const user = await getUserProfile(scheme.username)
			if (!user || !user.username) {
				throw new Error('No valid user')
			}
			return {
				to: '/u/$username',
				params: {
					username: user.username,
				},
			}
		}
		case 'STORY':
			// @todo
			toastWIP()
			throw new Error('Not implemented')
		case 'ORIGINAL_POST': {
			const post = await fetchOriginalPost(scheme.id)
			const postId = post.id
			const username = post.user?.username
			const commentId = scheme.commentId
			const type = post?.type && POST_TYPE_TO_ROUTE_MAPPING[post.type]

			if (!post || !username || !type) {
				throw new Error('No valid post')
			}

			return {
				to: commentId ? '/u/$username/$type/$id/$commentId' : '/u/$username/$type/$id',
				params: {
					username,
					id: postId,
					commentId,
					type,
				},
			}
		}
		case 'REPOST': {
			const post = await fetchRepost(scheme.id)
			const username = post.user?.username
			const type = post?.type && POST_TYPE_TO_ROUTE_MAPPING[post.type]
			if (!post || !username || !type) {
				throw new Error('No valid repost')
			}
			return {
				to: '/u/$username/$type/$id',
				params: {
					username,
					id: post.id,
					type,
				},
			}
		}
		case 'COMMENT': {
			const commentId = scheme.id
			const post = await getUniversalPostFetcher(scheme.targetType)?.(scheme.targetId)
			const username = post?.user?.username
			const type = post?.type && POST_TYPE_TO_ROUTE_MAPPING[post.type]

			if (!post || !username || !type) {
				throw new Error('No valid post or comment')
			}

			return {
				to: '/u/$username/$type/$id/$commentId',
				params: {
					username,
					id: post.id,
					commentId,
					type,
				},
			}
		}
		default:
			throw new Error('Unsupported Scheme Type')
	}
}

export default function useNotificationClickHandler(link: string | undefined) {
	const navigate = useUniversalNavigate()
	const handleClick = useCallback(
		async (e: MouseEvent) => {
			try {
				const options = await getNaviOptions(link)
				navigate(options, e)
			} catch (e) {
				return
			}
		},
		[navigate, link]
	)

	return useMemo(
		() => ({
			handleClick,
			isClickable: !!link,
		}),
		[handleClick, link]
	)
}
